import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { AGENT_TOOLS, SCAN_EVENT, type Species } from "@/types";
import type { PlantCandidate } from "@/server/lib/types";
import { client, streamSSEPost, unwrap } from "@/app/lib/client";
import { BackIcon, SparkleIcon, XIcon } from "@/app/components/system/Icons";
import { Button } from "@/app/components/system/Button";
import { ScreenHead } from "@/app/components/ScreenHead";
import { PotIllustration } from "@/app/components/PotIllustration";
import { CameraScanIllustration, CareTagIllustration, SpeciesSearchIllustration } from "@/app/components/AddActionIllustrations";
import { usePots } from "../lib/data-context";

type RecognizeSource = "label" | "plant";

// Label scans resolve to a single decoded species; photo scans resolve to a set
// of candidates the user must still pick from on the next screen.
type RecognizeOutcome =
  | { kind: "label"; decoded: Omit<Species, "id"> & { id?: string } }
  | { kind: "plant"; candidates: PlantCandidate[]; photo: { id: string; url: string } | null };

interface ScanProgress {
  label: string;
  percent: number;
}

const LABEL_STAGES: Record<string, ScanProgress> = {
  uploading: { label: "Uploading label…", percent: 10 },
  uploaded: { label: "Sending to vision model…", percent: 25 },
  identify: { label: "Reading the label…", percent: 35 },
  [`${AGENT_TOOLS.SEARCH_SPECIES}:call`]: { label: "Looking up species…", percent: 50 },
  [`${AGENT_TOOLS.SEARCH_SPECIES}:result`]: { label: "Looking up species…", percent: 55 },
  [`${AGENT_TOOLS.GET_SPECIES_GROWTH_DETAILS}:call`]: { label: "Fetching care details…", percent: 65 },
  [`${AGENT_TOOLS.GET_SPECIES_GROWTH_DETAILS}:result`]: { label: "Fetching care details…", percent: 70 },
  synthesize: { label: "Drafting recommendations…", percent: 80 },
  wrapping: { label: "Wrapping up…", percent: 95 },
};

type Handler = (data: Record<string, unknown>, ctx: HandlerCtx) => void;
interface HandlerCtx {
  onProgress: (p: ScanProgress) => void;
  setResult: (r: { species: Species | null }) => void;
  setError: (msg: string) => void;
}

const stageFromKey = (key: string, ctx: HandlerCtx) => {
  const next = LABEL_STAGES[key];
  if (next) ctx.onProgress(next);
};

const HANDLERS: Record<string, Handler> = {
  [SCAN_EVENT.STAGE]: (data, ctx) => stageFromKey(String(data["stage"] ?? ""), ctx),
  [SCAN_EVENT.TOOL_CALL]: (data, ctx) => stageFromKey(`${String(data["name"])}:call`, ctx),
  [SCAN_EVENT.TOOL_RESULT]: (data, ctx) => stageFromKey(`${String(data["name"])}:result`, ctx),
  [SCAN_EVENT.FINAL]: (_data, ctx) => stageFromKey("wrapping", ctx),
  [SCAN_EVENT.DONE]: (data, ctx) => ctx.setResult(data as { species: Species | null }),
  [SCAN_EVENT.ERROR]: (data, ctx) => ctx.setError(String(data["message"] ?? "Scan failed")),
};

async function callCareTagStreaming(
  file: File,
  onProgress: (p: ScanProgress) => void,
): Promise<{ species: Species | null }> {
  const fd = new FormData();
  fd.append("image", file);
  let result: { species: Species | null } | null = null;
  let serverError: string | null = null;
  const ctx: HandlerCtx = {
    onProgress,
    setResult: (r) => { result = r; },
    setError: (msg) => { serverError = msg; },
  };
  for await (const msg of streamSSEPost("/api/assistants/care-tag-recognition", fd)) {
    HANDLERS[msg.event]?.(msg.data as Record<string, unknown>, ctx);
  }
  if (serverError) throw new Error(serverError);
  if (!result) throw new Error("Scan stream ended without a result");
  return result;
}

async function callPlantRecognition(file: File) {
  return unwrap(client.api["assistants"]["plant-recognition"].$post({ form: { image: file } }));
}

async function recognize(
  file: File,
  source: RecognizeSource,
  onProgress: (p: ScanProgress) => void,
): Promise<RecognizeOutcome> {
  if (source === "label") {
    const { species } = await callCareTagStreaming(file, onProgress);
    if (species === null) {
      throw new Error("Couldn't decode any plant from the label. Try cropping closer to the text and ensuring good lighting?");
    }
    return { kind: "label", decoded: species };
  }
  const { candidates, photoImageId, photoImageUrl } = await callPlantRecognition(file);
  if (candidates.length === 0) {
    throw new Error("Couldn't identify your plant. Try a clearer photo with the whole plant in frame?");
  }
  const photo = photoImageId && photoImageUrl ? { id: photoImageId, url: photoImageUrl } : null;
  return { kind: "plant", candidates, photo };
}

const BIG_ACTION_BUTTON =
  "w-full flex items-center gap-3.5 p-[18px] border rounded-[18px] cursor-pointer text-left mb-2.5 transition-all duration-150 hover:-translate-y-px bg-paper border-line text-ink hover:border-moss-2";
const BIG_ACTION_ILLU = "relative w-[70px] h-16 rounded-2xl bg-cream-2 grid place-items-center shrink-0 overflow-hidden";
const BIG_ACTION_BADGE = "absolute bottom-1 right-1 w-5 h-5 rounded-full bg-paper text-moss grid place-items-center border-[1.5px] border-line";

interface BigActionButtonProps {
  illustration: React.ReactNode;
  badge?: React.ReactNode;
  title: string;
  subtitle: string;
  onClick: () => void;
}

function BigActionButton({ illustration, badge, title, subtitle, onClick }: BigActionButtonProps) {
  return (
    <button type="button" className={BIG_ACTION_BUTTON} onClick={onClick}>
      <span className={BIG_ACTION_ILLU}>
        {illustration}
        {badge ? <span className={BIG_ACTION_BADGE} aria-hidden="true">{badge}</span> : null}
      </span>
      <span className="flex-1 flex flex-col gap-0.5">
        <strong className="text-base font-medium">{title}</strong>
        <em className="not-italic text-[12.5px] opacity-75 leading-[1.4]">{subtitle}</em>
      </span>
      <BackIcon size={18} />
    </button>
  );
}

export function AddPlantScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultPotId = searchParams.get("potId") ?? undefined;
  const sourceParam = searchParams.get("source");
  const { pots } = usePots();
  const targetPot = defaultPotId && pots ? pots.find((x) => x.id === defaultPotId) : null;

  const [phase, setPhase] = useState<"idle" | "reading" | "error">("idle");
  const [source, setSource] = useState<RecognizeSource>("label");
  const [scanProgress, setScanProgress] = useState<ScanProgress>({ label: "", percent: 0 });
  const [error, setError] = useState<string | null>(null);
  const labelFileRef = useRef<HTMLInputElement | null>(null);
  const plantFileRef = useRef<HTMLInputElement | null>(null);
  const autoOpenedRef = useRef(false);

  // Plant recognition still uses the non-streaming endpoint, so synthesize a
  // ticker for that path. Label scans drive progress from real SSE events.
  useEffect(() => {
    if (phase !== "reading" || source !== "plant") return;
    const tick = setInterval(() => {
      setScanProgress((prev) => ({
        label: "Identifying plant…",
        percent: Math.min(95, prev.percent + 4 + Math.random() * 5),
      }));
    }, 140);
    return () => clearInterval(tick);
  }, [phase, source]);

  const updateProgress = (next: ScanProgress) =>
    setScanProgress((prev) =>
      prev.percent === next.percent && prev.label === next.label ? prev : next,
    );

  const openPicker = (next: RecognizeSource) => {
    setError(null);
    setSource(next);
    const ref = next === "label" ? labelFileRef : plantFileRef;
    if (ref.current) {
      ref.current.value = "";
      ref.current.click();
    }
  };

  // From onboarding: open the camera/file picker for the chosen enrollment path.
  useEffect(() => {
    if (autoOpenedRef.current) return;
    if (sourceParam !== "label" && sourceParam !== "plant") return;
    autoOpenedRef.current = true;
    openPicker(sourceParam);
  }, [sourceParam]);

  const onFile = (recognizeSource: RecognizeSource) => async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSource(recognizeSource);
    setPhase("reading");
    setScanProgress({
      label: recognizeSource === "label" ? "Uploading label…" : "Identifying plant…",
      percent: 0,
    });
    setError(null);
    try {
      const outcome = await recognize(file, recognizeSource, updateProgress);
      setScanProgress({ label: "Done", percent: 100 });
      setTimeout(() => {
        if (outcome.kind === "plant") {
          // Photo path: let the user pick among the proposed candidates.
          navigate("/identify", {
            state: { candidates: outcome.candidates, photo: outcome.photo, defaultPotId },
          });
        } else {
          navigate("/scan-result", {
            state: { result: outcome.decoded, defaultPotId, fromKnown: false, fromManual: false, source: "label", photoUpload: null },
          });
        }
      }, 200);
    } catch (err) {
      setPhase("error");
      setError((err as Error).message || String(err));
    }
  };

  return (
    <div className="screen pad">
      <ScreenHead
        title="Add"
        subtitle={targetPot ? `Adding to ${targetPot.name}` : "Pick how you want to start."}
        onBack={() => targetPot ? navigate(`/pots/${targetPot.id}`) : navigate("/")} />

      <BigActionButton
        illustration={<CameraScanIllustration />}
        title="Take a picture"
        subtitle="Snap your plant — we'll try to identify it for you."
        onClick={() => openPicker("plant")}
      />

      <BigActionButton
        illustration={<CareTagIllustration />}
        title="Scan a care tag"
        subtitle="Pick a photo of the plant's label — we'll fill in care details automatically."
        onClick={() => openPicker("label")}
      />

      <BigActionButton
        illustration={<SpeciesSearchIllustration />}
        title="Search Species"
        subtitle="Look up a species by name and add it to your garden."
        onClick={() => navigate("/species-search" + (defaultPotId ? `?potId=${defaultPotId}` : ""))}
      />

      {!targetPot && (
        <BigActionButton
          illustration={<PotIllustration shape="terracotta" plants={[]} size="small" />}
          title="Add a pot"
          subtitle="Group plants by container."
          onClick={() => navigate("/pots/new")}
        />
      )}

      <input ref={labelFileRef} type="file" accept="image/*" hidden onChange={onFile("label")} />
      <input ref={plantFileRef} type="file" accept="image/*" hidden onChange={onFile("plant")} />

      {phase === "error" && error && (
        <div
          className="mt-3 mb-1.5 mx-0 px-3.5 py-3 border rounded-2xl flex items-center justify-between gap-3 font-sans text-[13px] text-ink"
          style={{ background: "oklch(0.95 0.04 28 / 0.6)", borderColor: "oklch(0.85 0.08 28 / 0.5)" }}
        >
          <span className="inline-flex items-center gap-1.5">
            <XIcon size={14} /> {source === "label" ? "Couldn't decode label" : "Couldn't identify plant"}: {error}
          </span>
          <Button variant="secondary" onClick={() => openPicker(source)}>Try again</Button>
        </div>
      )}

      {phase === "reading" && (
        <div
          className="fixed inset-0 z-200 grid place-items-center backdrop-blur-xs"
          role="status"
          aria-live="polite"
          style={{ background: "oklch(0.18 0.02 145 / 0.55)", animation: "scan-overlay-fade 0.18s ease-out" }}
        >
          <div
            className="pt-7 pb-5.5 px-5.5 bg-paper border border-line rounded-[22px] flex flex-col items-center text-center gap-3 w-[min(86vw,320px)]"
            style={{ boxShadow: "0 30px 60px -20px oklch(0 0 0 / 0.35)" }}
          >
            <div
              className="w-14 h-14 rounded-full bg-moss-soft border border-line grid place-items-center text-moss"
              style={{ animation: "scan-overlay-pulse 1.4s ease-in-out infinite" }}
            >
              <SparkleIcon size={20} />
            </div>
            <p className="m-0 font-sans text-base font-medium text-ink">{scanProgress.label || (source === "label" ? "Reading label…" : "Identifying plant…")}</p>
            <div className="w-full h-1.5 rounded-[3px] bg-cream-2 overflow-hidden">
              <div
                className="h-full bg-moss rounded-[3px]"
                style={{ width: `${scanProgress.percent}%`, transition: "width 0.18s linear" }}
              />
            </div>
            <p className="m-0 font-sans text-xs font-medium text-ink-3 tracking-[0.04em]">{Math.round(scanProgress.percent)}%</p>
          </div>
        </div>
      )}
    </div>
  );
}
